import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Mail, MapPin, Scan, LockOpen, VolumeX,
  Wrench, CreditCard, Banknote, Linkedin, PhoneCall, Plane, Clock,
  Briefcase, Wifi, Zap, Volume2, Ruler, Accessibility, ShieldCheck,
  Users, Award, Sparkles, TrendingUp,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import DeckEmailGate from "@/components/DeckEmailGate";
import { supabase } from "@/integrations/supabase/client";

import casestudy2 from "@/assets/deck/casestudy-2.jpg";
import casestudy3 from "@/assets/deck/casestudy-3.jpg";
import casestudy4 from "@/assets/deck/casestudy-4.jpg";
import podInSitu from "@/assets/deck/pod-in-situ.jpeg";
import slide1Bg from "@/assets/deck/slide1-bg.jpg";
import qwipFounders from "@/assets/deck/qwip-founders.jpg";

// ─── Shared ──────────────────────────────────────────
const QwipLogo = ({ light = false }: { light?: boolean }) => (
  <div className={`absolute top-6 right-6 z-20 font-funnel font-bold text-xl ${light ? "text-white" : "text-foreground"}`}>
    qwip
  </div>
);

// ─── Slide 1: Cover ──────────────────────────────────
const SlideCover = () => (
  <div
    className="flex flex-col items-center justify-center h-full text-center px-8 laptop:px-24 desktop:px-32 relative"
    style={{ backgroundImage: `url(${slide1Bg})`, backgroundSize: "cover", backgroundPosition: "center" }}
  >
    <div className="absolute inset-0 bg-black/30" />
    <motion.h1
      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
      className="font-funnel font-bold text-7xl tablet:text-8xl laptop:text-9xl text-white leading-[0.9] tracking-tight mb-6 relative z-10"
    >
      qwip
    </motion.h1>
    <motion.p
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
      className="text-xl tablet:text-2xl laptop:text-3xl text-white/90 max-w-3xl font-funnel leading-relaxed relative z-10"
    >
      private, bookable spaces for calls and focused work -<br className="hidden laptop:block" /> wherever passengers need them.
    </motion.p>
    <motion.p
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
      className="text-sm text-white/60 font-sans mt-10 relative z-10"
    >
      airports · a proposal from qwip
    </motion.p>
  </div>
);

// ─── Slide 2: The Problem ────────────────────────────
const SlideProblem = () => {
  const needs = [
    { icon: PhoneCall, text: "take a confidential or important call" },
    { icon: Users, text: "join a last-minute meeting between gates" },
    { icon: Briefcase, text: "get focused work done before boarding" },
    { icon: Clock, text: "find quiet during a long layover or delay" },
  ];
  return (
    <div className="flex flex-col justify-center h-full px-8 laptop:px-20 desktop:px-28 py-12">
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
        className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-sans font-bold mb-6">
        the problem
      </motion.p>
      <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-6xl text-foreground leading-[1.02] tracking-tight mb-8 max-w-4xl">
        work happens everywhere.<br />
        <span className="font-cormorant italic font-normal">private space rarely does.</span>
      </motion.h2>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        className="text-lg laptop:text-xl text-muted-foreground font-funnel leading-relaxed max-w-2xl mb-10">
        passengers may need to:
      </motion.p>
      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4 max-w-3xl mb-10">
        {needs.map((n, i) => (
          <motion.div key={n.text} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 + i * 0.1 }}
            className="flex items-center gap-4 bg-foreground/5 border border-foreground/10 rounded-2xl p-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-foreground text-primary-foreground flex items-center justify-center">
              <n.icon className="w-4 h-4" />
            </div>
            <p className="font-funnel text-foreground text-base laptop:text-lg leading-snug">{n.text}</p>
          </motion.div>
        ))}
      </div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
        className="font-funnel text-lg laptop:text-xl text-foreground leading-relaxed max-w-3xl border-l-2 border-primary pl-5">
        current alternatives (lounges, gate seating, restrooms) are noisy, expensive or inconvenient - and passengers remember the airport experience during the moments things go wrong.
      </motion.p>
    </div>
  );
};

// ─── Slide 3: The qwip Solution ──────────────────────
const SlideSolution = () => {
  const specs = [
    { icon: Ruler, label: "1m × 1m footprint" },
    { icon: Users, label: "single-occupancy" },
    { icon: Wifi, label: "wifi included" },
    { icon: Zap, label: "usb-c + power" },
    { icon: Volume2, label: "soundproofed" },
    { icon: Accessibility, label: "step-free access" },
    { icon: ShieldCheck, label: "fire-rated materials" },
    { icon: Sparkles, label: "ventilation + lighting" },
  ];
  return (
    <div className="grid grid-cols-1 laptop:grid-cols-2 h-full">
      <div className="flex flex-col justify-center px-8 laptop:px-16 desktop:px-20 py-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-sans font-bold mb-4">
          the qwip solution
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-5xl text-foreground leading-[1.02] tracking-tight mb-6">
          soundproof pods, <span className="font-cormorant italic font-normal">bookable on-demand.</span>
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
          className="text-base laptop:text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg mb-8">
          a private space for calls, meetings, or focused work - available in short bursts, with no reservation or membership required.
        </motion.p>
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          {specs.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 + i * 0.05 }}
              className="flex items-center gap-3 border border-foreground/10 rounded-xl px-3 py-2.5">
              <s.icon className="w-4 h-4 text-primary flex-shrink-0" />
              <p className="font-funnel text-foreground text-sm">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="hidden laptop:flex items-center justify-center p-8">
        <motion.img
          initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          src={podInSitu} alt="qwip pod"
          className="w-full h-full object-cover rounded-3xl max-h-[80vh] shadow-xl"
        />
      </div>
    </div>
  );
};

// ─── Slide 4: Benefits for the Airport ───────────────
const SlideBenefits = () => {
  const benefits = [
    { icon: Plane, title: "better passenger experience", desc: "give travellers a private option during layovers, delays, and last-minute work moments." },
    { icon: Award, title: "innovation credentials", desc: "a genuine, measurable amenity for 'best airport' and passenger-experience awards." },
    { icon: TrendingUp, title: "monetise underused space", desc: "1m × 1m turns dead corners into recurring revenue - with zero upfront cost." },
    { icon: Wrench, title: "we handle everything", desc: "install, maintenance, payments, customer support and reporting - all managed by qwip." },
    { icon: Banknote, title: "revenue share", desc: "you earn a share of every booking, or opt for a fixed lease. no operational burden." },
    { icon: Users, title: "dwell-time uplift", desc: "passengers stay airside longer - more likely to spend at nearby retail and f&b." },
  ];
  return (
    <div className="flex flex-col h-full px-8 laptop:px-16 desktop:px-20 py-12 justify-center bg-primary relative overflow-y-auto">
      <QwipLogo light />
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
        className="text-xs uppercase tracking-[0.3em] text-white/50 font-sans font-bold mb-4">
        benefits for the airport
      </motion.p>
      <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-5xl text-white leading-[1.02] tracking-tight mb-10">
        why airports partner with qwip.
      </motion.h2>
      <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 gap-4 laptop:gap-6">
        {benefits.map((b, i) => (
          <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.08 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
            <div className="w-10 h-10 rounded-xl bg-white text-primary flex items-center justify-center mb-4">
              <b.icon className="w-5 h-5" />
            </div>
            <p className="font-funnel font-bold text-white text-lg mb-2">{b.title}</p>
            <p className="text-white/70 font-funnel text-sm leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ─── Slide 5: Proven Demand ──────────────────────────
const testimonials = [
  { quote: "I had to catch my Eurostar train in an hour, and had nowhere private to take an important client call. This was exactly what I needed in the moment.", author: "Adnaan, Consultant" },
  { quote: "I need this EVERY DAY. I work from cafes, hotel lobbies, wherever I can find a seat, but I always struggle with the noise.", author: "Rachael, 24, Law Student" },
  { quote: "I hope these pop up everywhere. As a parent who works remotely, this would make my life so much easier.", author: "Mark, 39, Brand Agency Owner" },
];
const SlideProof = () => {
  const [i, setI] = useState(0);
  return (
    <div className="grid grid-cols-1 laptop:grid-cols-2 h-full">
      <div className="flex flex-col justify-center px-8 laptop:px-16 desktop:px-20 py-8">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-sans font-bold mb-4">
          proven demand
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="font-funnel font-bold text-4xl laptop:text-5xl text-foreground leading-[1.02] tracking-tight mb-6">
          our first pod is live at <span className="font-cormorant italic font-normal">Westfield White City.</span>
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="text-base text-muted-foreground font-funnel leading-relaxed max-w-lg mb-6">
          we also ran an earlier trial at King's Cross Station - the busiest transport hub in the UK. the results validated the concept in a travel context:
        </motion.p>
        <div className="flex gap-3 mb-6 max-w-lg">
          {[
            { value: ">£100", label: "revenue in a single day" },
            { value: "~60%", label: "utilisation rate" },
          ].map((item, idx) => (
            <motion.div key={item.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + idx * 0.12 }}
              className="flex-1 bg-foreground text-primary-foreground rounded-2xl p-4">
              <p className="font-funnel font-bold text-2xl laptop:text-3xl mb-1">{item.value}</p>
              <p className="text-primary-foreground/70 font-sans text-xs">{item.label}</p>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
          className="max-w-lg">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}
              className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
              <p className="font-sans text-foreground text-sm laptop:text-base leading-relaxed mb-3">"{testimonials[i].quote}"</p>
              <p className="font-sans text-xs text-muted-foreground font-semibold uppercase tracking-wider">{testimonials[i].author}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex gap-2 mt-4">
            {testimonials.map((_, idx) => (
              <button key={idx} onClick={(e) => { e.stopPropagation(); setI(idx); }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === i ? "bg-foreground w-5" : "bg-foreground/20 hover:bg-foreground/40"}`} />
            ))}
          </div>
        </motion.div>
      </div>
      <div className="hidden laptop:flex items-center justify-center p-6">
        <motion.img
          initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          src={qwipFounders} alt="qwip pod at King's Cross"
          className="w-full h-full object-cover rounded-3xl max-h-[85vh]"
        />
      </div>
    </div>
  );
};

// ─── Slide 6: Market Validation ──────────────────────
const SlideMarket = () => (
  <div className="flex flex-col h-full px-8 laptop:px-16 desktop:px-20 py-12 justify-center relative">
    <QwipLogo />
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
      className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-sans font-bold mb-4">
      market validation
    </motion.p>
    <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
      className="font-funnel font-bold text-4xl laptop:text-5xl text-foreground leading-[1.02] tracking-tight mb-10">
      already proven across the world's biggest travel hubs.
    </motion.h2>
    <div className="grid grid-cols-1 laptop:grid-cols-3 gap-6 laptop:gap-8">
      {[
        { img: casestudy3, country: "United States", stat: "50+", description: "booths across major airport terminals and hotel lobbies (Minute Suites, JetBlue, Delta)." },
        { img: casestudy2, country: "Japan", stat: "400+", description: "booths deployed across railway stations and airports by CocoDesk and Telecube." },
        { img: casestudy4, country: "Middle East", stat: "30+", description: "booths across DXB, retail hubs and fitness clubs - a widely accepted amenity." },
      ].map((item, i) => (
        <motion.div key={item.country} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 + i * 0.15 }}
          className="flex flex-row laptop:flex-col gap-4 items-start">
          <div className="w-20 h-20 laptop:w-full laptop:h-auto laptop:aspect-[4/3] rounded-2xl overflow-hidden flex-shrink-0">
            <img src={item.img} alt={item.country} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1 laptop:mb-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <p className="font-funnel font-bold text-foreground text-lg">{item.country}</p>
            </div>
            <p className="text-muted-foreground text-sm font-funnel leading-relaxed">
              <span className="font-bold text-foreground">{item.stat}</span> {item.description}
            </p>
          </div>
        </motion.div>
      ))}
    </div>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
      className="mt-10 text-sm text-muted-foreground font-funnel italic max-w-3xl">
      the concept is validated. the opportunity in the UK is to bring the same amenity to airports here - first.
    </motion.p>
  </div>
);

// ─── Slide 7: How It Works ───────────────────────────
const SlideHow = () => {
  const cols = [
    {
      heading: "qwip provides",
      items: [
        { icon: Wrench, title: "pod + installation", desc: "delivered and fitted at zero cost to the airport." },
        { icon: CreditCard, title: "booking + payment", desc: "our platform handles bookings, pricing, and transactions." },
        { icon: ShieldCheck, title: "operations + support", desc: "maintenance, cleaning schedule, and 24/7 customer support." },
        { icon: TrendingUp, title: "marketing + reporting", desc: "monthly performance dashboards and joint-marketing collateral." },
      ],
    },
    {
      heading: "the airport provides",
      items: [
        { icon: Ruler, title: "floor space", desc: "a 1m × 1m airside footprint - unused corner or dwell zone." },
        { icon: Zap, title: "power + connectivity", desc: "standard mains socket and airport wifi access." },
        { icon: Clock, title: "operating hours", desc: "access during agreed terminal opening hours." },
      ],
    },
    {
      heading: "the passenger",
      items: [
        { icon: Scan, title: "scans", desc: "scans the QR code on the pod." },
        { icon: LockOpen, title: "books + unlocks", desc: "pays by the minute and unlocks with an access code." },
        { icon: VolumeX, title: "uses it", desc: "takes their call, meeting or focused-work session in private." },
      ],
    },
  ];
  return (
    <div className="flex flex-col h-full px-8 laptop:px-14 desktop:px-16 py-8 overflow-y-auto">
      <div className="text-center mb-8">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-sans font-bold mb-3">
          how it works
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="font-funnel font-bold text-3xl laptop:text-4xl text-foreground tracking-tight">
          a partnership with clear responsibilities.
        </motion.h2>
      </div>
      <div className="grid grid-cols-1 laptop:grid-cols-3 gap-6 laptop:gap-8 flex-1">
        {cols.map((col, ci) => (
          <motion.div key={col.heading} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 + ci * 0.12 }}
            className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5 laptop:p-6">
            <p className="text-[11px] uppercase tracking-[0.25em] text-primary font-sans font-bold mb-5">{col.heading}</p>
            <div className="space-y-4">
              {col.items.map((item) => (
                <div key={item.title} className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-foreground text-primary-foreground flex items-center justify-center">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-funnel font-bold text-foreground text-base">{item.title}</p>
                    <p className="text-muted-foreground text-xs laptop:text-sm font-sans leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ─── Slide 8: Contact & Next Steps ───────────────────
const SlideContact = () => {
  const steps = [
    "select a suitable terminal location",
    "conduct a site assessment together",
    "agree the partnership terms",
    "install and launch",
  ];
  return (
    <div className="grid grid-cols-1 laptop:grid-cols-[1.1fr_1fr] h-full">
      <div className="flex flex-col justify-center px-8 laptop:px-16 desktop:px-20 py-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-sans font-bold mb-4">
          next step
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="font-funnel font-bold text-4xl laptop:text-5xl desktop:text-6xl text-foreground leading-[1.02] tracking-tight mb-6">
          offer your passengers private space -<br />
          <span className="font-cormorant italic font-normal">without the upfront cost or operational burden.</span>
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="text-base laptop:text-lg text-muted-foreground font-funnel leading-relaxed max-w-xl mb-8">
          let's identify a suitable location for an initial qwip pilot at your airport.
        </motion.p>
        <div className="space-y-3 mb-8 max-w-lg">
          {steps.map((s, i) => (
            <motion.div key={s} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.08 }}
              className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground font-funnel font-bold text-sm flex items-center justify-center flex-shrink-0">
                {i + 1}
              </div>
              <p className="font-funnel text-foreground text-base laptop:text-lg">{s}</p>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
          className="flex flex-col gap-3 items-start">
          <a href="https://wa.me/447347474059" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 bg-foreground text-primary-foreground px-7 py-3.5 rounded-full font-sans font-semibold text-base hover:opacity-90 transition-opacity">
            <FaWhatsapp className="w-5 h-5" />
            get in touch
          </a>
          <a href="mailto:hello@qwip.co.uk"
            className="flex items-center gap-3 text-foreground font-sans font-medium text-sm hover:opacity-70 transition-opacity">
            <Mail className="w-4 h-4" />
            hello@qwip.co.uk
          </a>
          <div className="flex items-center gap-3 text-foreground font-sans font-medium text-sm">
            <Linkedin className="w-4 h-4" />
            <span>founders:&nbsp;
              <a href="https://www.linkedin.com/in/zain-hussain" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70 transition-opacity">Zain</a>
              &nbsp;&amp;&nbsp;
              <a href="https://www.linkedin.com/in/stiofanfh/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70 transition-opacity">Stiofán</a>
            </span>
          </div>
        </motion.div>
      </div>
      <div className="hidden laptop:flex items-center justify-center p-8 bg-primary">
        <div className="text-center">
          <p className="font-funnel font-bold text-6xl desktop:text-7xl text-white leading-[0.9] tracking-tight mb-4">qwip</p>
          <p className="font-funnel italic font-cormorant text-2xl text-white/80">private space, on demand.</p>
        </div>
      </div>
    </div>
  );
};

// ─── Slides array ────────────────────────────────────
const slides = [SlideCover, SlideProblem, SlideSolution, SlideBenefits, SlideProof, SlideMarket, SlideHow, SlideContact];

// ─── Page shell ──────────────────────────────────────
const DeckAirports = ({ skipEmailGate = false }: { skipEmailGate?: boolean }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showGate, setShowGate] = useState(!skipEmailGate);
  const [viewId, setViewId] = useState("");
  const startTimeRef = useRef(Date.now());
  const slidesVisitedRef = useRef<Set<number>>(new Set([0]));

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= slides.length) return;
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
    slidesVisitedRef.current.add(index);
  }, [current]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (!viewId) return;
    const interval = setInterval(async () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const slidesArr = Array.from(slidesVisitedRef.current);
      await supabase.from("deck_views").update({
        last_active_at: new Date().toISOString(),
        duration_seconds: duration,
        slides_viewed: slidesArr,
        total_slides_viewed: slidesArr.length,
      }).eq("id", viewId);
    }, 10000);
    return () => clearInterval(interval);
  }, [viewId]);

  const handleEmailSubmit = (_email: string, id: string) => {
    setViewId(id);
    startTimeRef.current = Date.now();
    setShowGate(false);
  };

  useEffect(() => {
    if (showGate) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "f" || e.key === "F") {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
        else document.exitFullscreen?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [next, prev, showGate]);

  useEffect(() => {
    if (showGate) return;
    let startX = 0;
    const handleTouchStart = (e: TouchEvent) => { startX = e.touches[0].clientX; };
    const handleTouchEnd = (e: TouchEvent) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [next, prev, showGate]);

  const CurrentSlide = slides[current];
  const isDarkSlide = [0, 3].includes(current);
  const arrowBg = isDarkSlide ? "bg-white/20 hover:bg-white/40" : "bg-foreground/10 hover:bg-foreground/20";
  const arrowIcon = isDarkSlide ? "text-white" : "text-foreground";
  const dotActive = isDarkSlide ? "bg-white" : "bg-foreground";
  const dotInactive = isDarkSlide ? "bg-white/50 hover:bg-white/70" : "bg-foreground/20 hover:bg-foreground/40";

  const variants = { enter: { opacity: 0 }, center: { opacity: 1 }, exit: { opacity: 0 } };

  return (
    <div className="h-screen w-screen bg-background overflow-hidden relative select-none">
      {showGate && <DeckEmailGate onSubmit={handleEmailSubmit} deckType="airports" />}

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={current} custom={direction} variants={variants}
          initial="enter" animate="center" exit="exit"
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <CurrentSlide />
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-foreground/5">
        <motion.div className="h-full bg-foreground"
          animate={{ width: `${((current + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4 }} />
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button key={i} onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === current ? `${dotActive} w-6 shadow-md` : dotInactive
              }`} />
          ))}
        </div>
      </div>

      {current > 0 && (
        <button onClick={prev}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}>
          <ChevronLeft className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}
      {current < slides.length - 1 && (
        <button onClick={next}
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}>
          <ChevronRight className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}

      <div className="absolute top-6 right-6 flex items-center gap-3">
        <span className={`text-xs font-sans ${isDarkSlide ? "text-white/50" : "text-muted-foreground/50"}`}>
          {current + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

export default DeckAirports;
